import {Directive, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {RouteService} from '@core/services/route.service';
import {debounce} from 'rxjs/operators';
import {Subscription, timer} from 'rxjs';

interface PaginationState {
    itemsAmount: number;
    loading: boolean;
}

interface PaginationConfig {
    useQueryParams?: boolean;
    autoLoad?: boolean;
    debounceTime?: number;
    countPersistentFilters?: boolean;
}

interface FieldMapping {
    size?: string;
    page?: string;
    search?: string;
    version?: string;
    sort?: string;
}

/**
 * Interface for multiple column sorting state.
 */
interface MultiSortState {
    field: string;
    direction: 'asc' | 'desc';
    priority: number; // Para ordenar a prioridade
}

/**
 * Abstract base class for implementing pagination in Angular components.
 * Provides complete pagination functionality, filters and query parameters integration.
 *
 * @abstract
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @example
 * ```typescript
 * export class UserListComponent extends AppPaginationBaseAbstract {
 *   onInit(): void {
 *     this.addCustomFilters({ nome: '', status: 'ativo' });
 *     this.setPersistentFilters(['status']);
 *   }
 *
 *   findAll(filters: any): void {
 *     this.userService.findAll(filters).subscribe(result => {
 *       this.setPaginationData(result.total);
 *     });
 *   }
 * }
 * ```
 */
@Directive()
export abstract class AppPaginationBaseAbstract implements OnInit, OnDestroy {
    // Constants
    private static readonly FIRST_DEBOUNCE_TIME = 50;
    private static readonly DEFAULT_DEBOUNCE_TIME = 500;
    private static readonly DEFAULT_PAGE_SIZE = 10;
    private static readonly MIN_PAGE = 1;

    // Dependency injections
    private readonly activatedRoute: ActivatedRoute;
    protected readonly routeService: RouteService;

    // Configurations
    protected config: PaginationConfig = {
        useQueryParams: true,
        autoLoad: true,
        debounceTime: AppPaginationBaseAbstract.DEFAULT_DEBOUNCE_TIME,
        countPersistentFilters: false
    };

    // Field mapping (allows customizing attribute names)
    protected fieldMapping: FieldMapping = {
        size: 'size',
        page: 'page',
        search: 'q',
        version: 'v',
        sort: 'sort'
    };

    // Internal state
    private firstTime = true;
    private queryParams$?: Subscription;
    private initialFilters: any = {};

    // List of filters that should persist when resetting
    private persistentFilters: Set<string> = new Set();
    // Saved persistent values
    private persistentValues: any = {};
    // Parameters that should be ignored in URL (e.g., tabId)
    private ignoredParams: Set<string> = new Set(['tabId']);

    // Public properties
    currentPage = 1;
    protected currentSort: MultiSortState[] = [];
    pagination: PaginationState = this.getDefaultPagination();
    filters: any = this.getDefaultFilters();

    /**
     * Getter that ensures loading is never undefined.
     * @returns {boolean} Current loading state
     */
    get isLoading(): boolean {
        return this.pagination?.loading ?? false;
    }

    /**
     * Constructor for the pagination base class.
     *
     * @param {PaginationConfig} [config] - Optional pagination configuration
     * @param {FieldMapping} [fieldMapping] - Custom field mapping
     */
    protected constructor(config?: PaginationConfig, fieldMapping?: FieldMapping) {
        this.activatedRoute = inject(ActivatedRoute);
        this.routeService = inject(RouteService);

        if (config) {
            this.config = {...this.config, ...config};
        }

        if (fieldMapping) {
            this.fieldMapping = {...this.fieldMapping, ...fieldMapping};
        }
    }

    /**
     * Abstract method to fetch all records with applied filters.
     * Must be implemented by child classes.
     *
     * @abstract
     * @param {any} filters - Object containing all active filters
     */
    abstract findAll(filters: any): void;

    /**
     * Abstract method for custom initialization of child component.
     * Called during ngOnInit before applying filters.
     *
     * @abstract
     */
    abstract onInit(): void;

    /**
     * Defines which filters should persist when executing resetFilters().
     * Persistent filters maintain their values even after reset.
     *
     * @protected
     * @param {string[]} filterNames - Array with names of persistent filters
     *
     * @example
     * ```typescript
     * this.setPersistentFilters(['empresaId', 'status', 'departamento']);
     * ```
     */
    protected setPersistentFilters(filterNames: string[]): void {
        this.persistentFilters = new Set(filterNames);
        this.savePersistentValues();
    }

    /**
     * Adds one or multiple filters to the persistent filters list.
     *
     * @protected
     * @param {string | string[]} filterNameOrNames - Name(s) of the filter(s) to be marked as persistent
     *
     * @example
     * ```typescript
     * // Single filter
     * this.addPersistentFilter('empresaId');
     *
     * // Multiple filters at once
     * this.addPersistentFilter(['empresaId', 'status', 'departamento']);
     * ```
     */
    protected addPersistentFilter(filterNameOrNames: string | string[]): void {
        if (Array.isArray(filterNameOrNames)) {
            filterNameOrNames.forEach(name => this.persistentFilters.add(name));
        } else {
            this.persistentFilters.add(filterNameOrNames);
        }
        this.savePersistentValues();
    }

    /**
     * Removes a filter from the persistent filters list.
     *
     * @protected
     * @param {string} filterName - Name of the filter to be removed from persistent list
     *
     * @example
     * ```typescript
     * this.removePersistentFilter('departamento');
     * ```
     */
    protected removePersistentFilter(filterName: string): void {
        this.persistentFilters.delete(filterName);
        if (this.persistentValues.hasOwnProperty(filterName)) {
            delete this.persistentValues[filterName];
        }
    }

    /**
     * Checks if a specific filter is marked as persistent.
     *
     * @protected
     * @param {string} filterName - Name of the filter to be checked
     * @returns {boolean} True if the filter is persistent, false otherwise
     *
     * @example
     * ```typescript
     * if (this.isPersistentFilter('empresaId')) {
     *   console.log('EmpresaId is persistent');
     * }
     * ```
     */
    protected isPersistentFilter(filterName: string): boolean {
        return this.persistentFilters.has(filterName);
    }

    /**
     * Returns an array with all configured persistent filter names.
     *
     * @protected
     * @returns {string[]} Array with persistent filter names
     *
     * @example
     * ```typescript
     * const persistentFilters = this.getPersistentFilters();
     * console.log('Persistent filters:', persistentFilters);
     * ```
     */
    protected getPersistentFilters(): string[] {
        return Array.from(this.persistentFilters);
    }

    /**
     * Saves current values of persistent filters.
     * Internal method called automatically when needed.
     *
     * @private
     */
    private savePersistentValues(): void {
        this.persistentValues = {};
        this.persistentFilters.forEach(filterName => {
            if (this.filters.hasOwnProperty(filterName)) {
                this.persistentValues[filterName] = this.deepClone(this.filters[filterName]);
            }
        });
    }

    /**
     * Restores saved values of persistent filters.
     * Internal method used during filter reset.
     *
     * @private
     */
    private restorePersistentValues(): void {
        Object.keys(this.persistentValues).forEach(filterName => {
            if (this.persistentFilters.has(filterName)) {
                this.filters[filterName] = this.deepClone(this.persistentValues[filterName]);
            }
        });
    }

    /**
     * Performs a deep clone of an object.
     * Supports nested objects, arrays, dates and primitive types.
     *
     * @private
     * @param {any} obj - Object to be cloned
     * @returns {any} Deep copy of the object
     */
    private deepClone(obj: any): any {
        if (obj === null || typeof obj !== 'object') {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map(item => this.deepClone(item));
        }

        if (obj instanceof Date) {
            return new Date(obj.getTime());
        }

        const cloned: any = {};
        Object.keys(obj).forEach(key => {
            cloned[key] = this.deepClone(obj[key]);
        });

        return cloned;
    }

    /**
     * Allows dynamic configuration of pagination settings.
     *
     * @protected
     * @param {Partial<PaginationConfig>} config - Partial configuration object
     *
     * @example
     * ```typescript
     * this.setConfig({
     *   useQueryParams: false,
     *   debounceTime: 300,
     *   countPersistentFilters: false // Excludes persistent filters from count
     * });
     * ```
     */
    protected setConfig(config: Partial<PaginationConfig>): void {
        this.config = {...this.config, ...config};
    }

    /**
     * Allows dynamic configuration of field mapping.
     *
     * @protected
     * @param {Partial<FieldMapping>} fieldMapping - Partial field mapping object
     *
     * @example
     * ```typescript
     * this.setFieldMapping({ size: 'pageSize', page: 'pageNumber' });
     * ```
     */
    protected setFieldMapping(fieldMapping: Partial<FieldMapping>): void {
        this.fieldMapping = {...this.fieldMapping, ...fieldMapping};
    }

    /**
     * Returns default pagination state.
     *
     * @returns {PaginationState} Default pagination state object
     */
    getDefaultPagination(): PaginationState {
        return {
            itemsAmount: 0,
            loading: false,
        };
    }

    /**
     * Returns default filters object with standard pagination fields.
     *
     * @returns {any} Default filters object
     */
    getDefaultFilters(): any {
        const filters: any = {};

        filters[this.fieldMapping.size!] = AppPaginationBaseAbstract.DEFAULT_PAGE_SIZE;
        filters[this.fieldMapping.page!] = 0;
        filters[this.fieldMapping.search!] = '';
        filters[this.fieldMapping.version!] = '';

        return filters;
    }

    /**
     * Protected method to add custom filters in a clean way.
     *
     * @protected
     * @param {any} customFilters - Object containing custom filters to add
     *
     * @example
     * ```typescript
     * this.addCustomFilters({
     *   name: '',
     *   status: 'active',
     *   category: []
     * });
     * ```
     */
    protected addCustomFilters(customFilters: any): void {
        this.filters = {...this.filters, ...customFilters};
        this.savePersistentValues();
    }

    /**
     * Angular lifecycle hook - component initialization.
     * Executes the initialization flow: onInit() -> save state -> apply URL -> listen to changes.
     *
     * @public
     */
    ngOnInit(): void {
        // 1. Child defines custom filters and configurations
        this.onInit();

        // 2. Save initial state after child's onInit()
        this.saveInitialFiltersState();

        // 2.1. Save initial persistent values
        this.savePersistentValues();

        // 3. If using query params, apply URL and start listener
        if (this.config.useQueryParams) {
            this.applyUrlOnInit();
            this.listenQueryParams();
        } else if (this.config.autoLoad) {
            // If not using query params but auto load is active, call findAll
            this.findAll(this.filters);
        }
    }

    /**
     * Angular lifecycle hook - component destruction.
     * Cleans up subscriptions and calls optional cleanup methods.
     *
     * @public
     */
    ngOnDestroy(): void {
        this.onDestroy();
        this.unsubscribeFromQueryParams();
    }

    /**
     * Protected method for optional cleanup by child classes.
     * Called during component destruction.
     *
     * @protected
     */
    protected onDestroy(): void {
        // Child classes can override this method
    }

    /**
     * Protected method called when query parameters change.
     * Can be overridden by child classes for custom behavior.
     *
     * @protected
     * @param {any} data - Converted query parameters object
     */
    protected onNextQueryParams(data: any): void {
        // Child classes can override this method
    }

    /**
     * Hook called to normalize query parameters BEFORE they are converted.
     * Useful for ensuring certain fields are always arrays (e.g., multi-select filters).
     * Called BEFORE convertAndValidateParams.
     *
     * @protected
     * @param {any} params - Raw query parameters from URL
     * @returns {any} Normalized parameters
     *
     * @example
     * ```typescript
     * protected normalizeQueryParams(params: any): any {
     *   // Ensure negotiationType is always an array
     *   if (params.negotiationType && !Array.isArray(params.negotiationType)) {
     *     params.negotiationType = [params.negotiationType];
     *   }
     *   return params;
     * }
     * ```
     */
    protected normalizeQueryParams(params: any): any {
        return params;
    }

    /**
     * Sets the loading state for the pagination.
     *
     * @protected
     * @param {boolean} loading - Loading state to set
     */
    protected setLoading(loading: boolean): void {
        this.pagination.loading = loading;
    }

    /**
     * Sets pagination data after a successful search.
     * Updates total items count and sets loading to false.
     *
     * @protected
     * @param {number} itemsAmount - Total number of items found
     *
     * @example
     * ```typescript
     * this.userService.findAll(filters).subscribe(result => {
     *   this.setPaginationData(result.totalElements);
     * });
     * ```
     */
    protected setPaginationData(itemsAmount: number): void {
        this.pagination.itemsAmount = itemsAmount;
        this.pagination.loading = false;
    }

    /**
     * Navigates to a specific page.
     * Updates current page and triggers search if not using query params.
     *
     * @public
     * @param {number} page - Page number to navigate to (1-based)
     *
     * @example
     * ```typescript
     * this.paginate(3); // Goes to page 3
     * ```
     */
    paginate(page: number): void {
        if (!this.isValidPage(page)) {
            console.warn(`Invalid page: ${page}. Must be >= ${AppPaginationBaseAbstract.MIN_PAGE}`);
            return;
        }

        this.filters[this.fieldMapping.page!] = page - 1;
        this.currentPage = page;

        if (this.config.useQueryParams) {
            this.updateURL();
        } else {
            this.findAll(this.filters);
        }
    }

    /**
     * Changes the number of items per page and resets to first page.
     *
     * @public
     * @param {number} itemsPerPage - Number of items to display per page
     *
     * @example
     * ```typescript
     * this.changeItemsPerPage(25); // Shows 25 items per page
     * ```
     */
    changeItemsPerPage(itemsPerPage: number): void {
        if (!this.isValidPageSize(itemsPerPage)) {
            console.warn(`Invalid page size: ${itemsPerPage}`);
            return;
        }

        this.filters[this.fieldMapping.page!] = 0;
        this.filters[this.fieldMapping.size!] = itemsPerPage;
        this.currentPage = 1;

        if (this.config.useQueryParams) {
            this.updateURL();
        } else {
            this.findAll(this.filters);
        }
    }

    /**
     * Updates search with current filters, resets pagination and adds version.
     * Typically called when search button is clicked.
     *
     * @public
     */
    updateSearch(): void {
        this.resetPaginationState();
        this.filters[this.fieldMapping.page!] = 0;
        this.currentPage = 1;
        this.filters[this.fieldMapping.version!] = Date.now();
        if (this.config.useQueryParams) {
            this.updateURL();
        } else {
            this.findAll(this.filters);
        }
    }

    /**
     * Sets custom sort configuration with support for multiple fields.
     * Establishes sorting by multiple columns with priority order.
     *
     * @protected
     * @param {Array<{field: string, direction: 'asc' | 'desc'}>} sorts - Array of sort configurations
     *
     * @example
     * ```typescript
     * onInit(): void {
     *     this.addCustomSort([
     *         { field: 'status', direction: 'asc' },
     *         { field: 'createdDate', direction: 'desc' }
     *     ]);
     *     this.addCustomFilters({status: 'ACTIVE'});
     * }
     * ```
     */
    protected addCustomSort(sorts: Array<{ field: string, direction: 'asc' | 'desc' }>): void {
        this.currentSort = sorts.map((sort, index) => ({
            ...sort,
            priority: index + 1
        }));

        // Formato: "field1,direction1;field2,direction2"
        this.filters[this.fieldMapping.sort!] = sorts
            .map(sort => `${sort.field},${sort.direction}`)
            .join(';');
    }

    /**
     * Handles multi-column sorting by updating or adding to existing sort criteria.
     */
    protected onSort(sort: { field: string, direction: 'asc' | 'desc' }, addToExisting = false): void {
        if (!addToExisting) {
            // Substitui ordenação existente
            this.addCustomSort([sort]);
        } else {
            // Adiciona à ordenação existente
            const existing = this.currentSort || [];
            const existingIndex = existing.findIndex(s => s.field === sort.field);

            if (existingIndex >= 0) {
                // Atualiza ordenação existente do campo
                existing[existingIndex] = {...sort, priority: existing[existingIndex].priority};
            } else {
                // Adiciona novo campo
                existing.push({...sort, priority: existing.length + 1});
            }

            this.currentSort = existing;
            this.filters[this.fieldMapping.sort!] = existing
                .sort((a, b) => a.priority - b.priority)
                .map(s => `${s.field},${s.direction}`)
                .join(';');
        }

        this.updateSearch();
    }

    /**
     * Resets all filters to default values while preserving persistent filters.
     * Persistent filters maintain their current values after reset.
     *
     * @public
     *
     * @example
     * ```typescript
     * // If 'status' is persistent with value 'active'
     * this.resetFilters();
     * // After reset: status = 'active' (preserved), other filters = default values
     * ```
     */
    resetFilters(): void {
        // Save current values of persistent filters before reset
        this.savePersistentValues();

        // Identify all filters (default + custom)
        const allFilters = {...this.filters};
        const defaultFilters = this.getDefaultFilters();

        // Reset to defaults
        this.filters = {...defaultFilters, [this.fieldMapping.version!]: Date.now()};

        // For custom filters, set empty value instead of deleting
        Object.keys(allFilters).forEach(key => {
            if (!defaultFilters.hasOwnProperty(key)) {
                // Skip persistent filters
                if (this.persistentFilters.has(key)) {
                    return;
                }

                // Determine empty value based on current type
                const currentValue = allFilters[key];
                if (Array.isArray(currentValue)) {
                    this.filters[key] = []; // Empty array
                } else if (typeof currentValue === 'string') {
                    this.filters[key] = ''; // Empty string
                } else if (typeof currentValue === 'number') {
                    this.filters[key] = null; // Empty string for numbers
                } else if (typeof currentValue === 'boolean') {
                    this.filters[key] = false; // False for booleans
                } else {
                    this.filters[key] = null; // Null for other types
                }
            }
        });

        // Restore persistent filter values
        this.restorePersistentValues();

        this.currentPage = 1;
        this.resetPaginationState();

        if (this.config.useQueryParams) {
            this.replaceURL();
        } else {
            this.findAll(this.filters);
        }
    }

    /**
     * Clears a specific filter by name.
     * Cannot clear persistent filters - shows warning instead.
     *
     * @public
     * @param {string} filterName - Name of the filter to clear
     *
     * @example
     * ```typescript
     * this.clearFilter('name'); // Clears the 'name' filter
     * this.clearFilter('persistentFilter'); // Shows warning, doesn't clear
     * ```
     */
    clearFilter(filterName: string): void {
        if (this.filters.hasOwnProperty(filterName)) {
            // Check if filter is persistent
            if (this.persistentFilters.has(filterName)) {
                console.warn(`Cannot clear filter '${filterName}' as it is marked as persistent.`);
                return;
            }

            const defaultFilters = this.getDefaultFilters();

            if (defaultFilters.hasOwnProperty(filterName)) {
                // Default filter: return to default value
                this.filters[filterName] = defaultFilters[filterName];
            } else {
                // Custom filter: set empty value based on type
                const currentValue = this.filters[filterName];
                if (Array.isArray(currentValue)) {
                    this.filters[filterName] = []; // Empty array
                } else if (typeof currentValue === 'string') {
                    this.filters[filterName] = ''; // Empty string
                } else if (typeof currentValue === 'number') {
                    this.filters[filterName] = 0; // Zero for numbers
                } else if (typeof currentValue === 'boolean') {
                    this.filters[filterName] = false; // False for booleans
                } else {
                    this.filters[filterName] = null; // Null for other types
                }
            }

            if (this.config.useQueryParams) {
                this.updateURL();
            } else {
                this.findAll(this.filters);
            }
        }
    }

    /**
     * Executes search manually (useful when useQueryParams = false).
     *
     * @public
     *
     * @example
     * ```typescript
     * // Update filters then search manually
     * this.updateFilter('name', 'John');
     * this.search();
     * ```
     */
    search(): void {
        this.findAll(this.filters);
    }

    /**
     * Updates a single filter without triggering automatic search.
     * If using query params, updates URL. Otherwise, call search() manually.
     *
     * @public
     * @param {string} filterName - Name of the filter to update
     * @param {any} value - New value for the filter
     *
     * @example
     * ```typescript
     * this.updateFilter('status', 'active');
     * this.updateFilter('categories', ['tech', 'business']);
     * ```
     */
    updateFilter(filterName: string, value: any): void {
        this.filters[filterName] = value;

        // Update persistent values if it's a persistent filter
        if (this.persistentFilters.has(filterName)) {
            this.persistentValues[filterName] = this.deepClone(value);
        }

        if (this.config.useQueryParams) {
            this.updateURL();
        }
    }

    /**
     * Updates multiple filters at once.
     *
     * @public
     * @param {any} filters - Object containing multiple filter key-value pairs
     *
     * @example
     * ```typescript
     * this.updateFilters({
     *   name: 'John',
     *   status: 'active',
     *   category: 'tech'
     * });
     * ```
     */
    updateFilters(filters: any): void {
        Object.keys(filters).forEach(key => {
            this.filters[key] = filters[key];

            // Update persistent values if it's a persistent filter
            if (this.persistentFilters.has(key)) {
                this.persistentValues[key] = this.deepClone(filters[key]);
            }
        });

        if (this.config.useQueryParams) {
            this.updateURL();
        }
    }

    /**
     * Returns the total number of active filters (changed by user).
     * Persistent filters are included or excluded based on config.countPersistentFilters.
     *
     * @public
     * @returns {number} Total count of active filters
     *
     * @example
     * ```typescript
     * const activeCount = this.getActiveFiltersCount();
     * console.log(`${activeCount} filters are active`);
     * ```
     */
    /**
     * Returns the total number of active filters (changed by user).
     * Persistent filters are included or excluded based on config.countPersistentFilters.
     *
     * @public
     * @returns {number} Total count of active filters
     */
    getActiveFiltersCount(): number {
        let count = 0;

        Object.keys(this.filters).forEach(key => {
            if (this.isInternalField(key)) return;

            const isPersistent = this.persistentFilters.has(key);
            const value = this.filters[key];
            const initialValue = this.initialFilters[key];

            // Se for persistente
            if (isPersistent) {
                // Só conta se config permite e tem valor ativo
                if (this.config.countPersistentFilters && this.isCustomFilterActive(value)) {
                    count++;
                }
                return;
            }

            // Filtros não-persistentes
            const existedInitially = this.initialFilters.hasOwnProperty(key);
            const isActive = existedInitially
                ? this.isFilterActive(value, initialValue)
                : this.isCustomFilterActive(value);

            if (isActive) count++;
        });

        return count;
    }

    /**
     * Returns the number of default filters that have been changed by user.
     *
     * @public
     * @returns {number} Count of modified default filters
     */
    getDefaultFiltersCount(): number {
        let count = 0;

        Object.keys(this.initialFilters).forEach(key => {
            // Ignore internal control and pagination fields
            if (this.isInternalField(key)) return;

            const currentValue = this.filters[key];
            const initialValue = this.initialFilters[key];

            // Consider active if current value is different from initial
            if (this.isFilterActive(currentValue, initialValue)) {
                count++;
            }
        });

        return count;
    }

    /**
     * Returns the number of custom filters that have been added.
     *
     * @public
     * @returns {number} Count of active custom filters
     */
    getCustomFiltersCount(): number {
        let count = 0;

        Object.keys(this.filters).forEach(key => {
            // Ignore internal and pagination fields
            if (this.isInternalField(key)) return;

            // If didn't exist in initial state, it's a custom filter added
            if (!this.initialFilters.hasOwnProperty(key)) {
                const value = this.filters[key];

                // Consider active if has valid value
                if (this.isCustomFilterActive(value)) {
                    count++;
                }
            }
        });

        return count;
    }

    /**
     * Returns the number of active persistent filters.
     *
     * @public
     * @returns {number} Count of active persistent filters
     *
     * @example
     * ```typescript
     * const persistentCount = this.getPersistentFiltersCount();
     * console.log(`${persistentCount} persistent filters are active`);
     * ```
     */
    getPersistentFiltersCount(): number {
        let count = 0;

        this.persistentFilters.forEach(filterName => {
            if (this.filters.hasOwnProperty(filterName)) {
                const value = this.filters[filterName];
                if (this.isCustomFilterActive(value)) {
                    count++;
                }
            }
        });

        return count;
    }

    /**
     * Checks if a default filter is active (different from initial value).
     *
     * @private
     * @param {any} currentValue - Current filter value
     * @param {any} initialValue - Initial filter value
     * @returns {boolean} True if filter is active, false otherwise
     */
    private isFilterActive(currentValue: any, initialValue: any): boolean {
        // For strings, consider active if not empty and different from initial
        if (typeof currentValue === 'string' && typeof initialValue === 'string') {
            return currentValue.trim() !== '' && currentValue !== initialValue;
        }

        // For numbers, consider active if different from initial
        if (typeof currentValue === 'number' && typeof initialValue === 'number') {
            return currentValue !== initialValue;
        }

        // For other types, compare directly
        return currentValue !== initialValue;
    }

    /**
     * Checks if a custom filter is active.
     *
     * @private
     * @param {any} value - Filter value to check
     * @returns {boolean} True if filter is active, false otherwise
     */
    private isCustomFilterActive(value: any): boolean {
        if (value === undefined || value === null) {
            return false;
        }

        if (typeof value === 'string') {
            return value.trim() !== '';
        }

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        return true;
    }

    /**
     * Returns detailed information about active filters.
     *
     * @public
     * @returns {Object} Object containing filter statistics and lists
     * @returns {number} returns.total - Total number of active filters
     * @returns {number} returns.default - Number of active default filters
     * @returns {number} returns.custom - Number of active custom filters
     * @returns {number} returns.persistent - Number of active persistent filters
     * @returns {string[]} returns.activeFilters - List of all active filter names
     * @returns {string[]} returns.persistentFilters - List of active persistent filter names
     *
     * @example
     * ```typescript
     * const info = this.getFiltersInfo();
     * console.log(`Total: ${info.total}, Persistent: ${info.persistent}`);
     * console.log('Active filters:', info.activeFilters);
     * ```
     */
    getFiltersInfo(): {
        total: number;
        default: number;
        custom: number;
        persistent: number;
        activeFilters: string[];
        persistentFilters: string[];
    } {
        const activeFilters: string[] = [];
        const persistentActiveFilters: string[] = [];
        let defaultCount = 0;
        let customCount = 0;
        let persistentCount = 0;

        // Check filters that existed in initial state
        Object.keys(this.initialFilters).forEach(key => {
            // Ignore internal control and pagination fields
            if (this.isInternalField(key)) return;

            const currentValue = this.filters[key];
            const initialValue = this.initialFilters[key];

            if (this.isFilterActive(currentValue, initialValue)) {
                if (this.persistentFilters.has(key)) {
                    persistentCount++;
                    persistentActiveFilters.push(key);
                } else {
                    defaultCount++;
                }
                activeFilters.push(key);
            }
        });

        // Check custom filters (didn't exist initially)
        Object.keys(this.filters).forEach(key => {
            // Ignore internal and pagination fields
            if (this.isInternalField(key)) return;

            if (!this.initialFilters.hasOwnProperty(key)) {
                const value = this.filters[key];
                if (this.isCustomFilterActive(value)) {
                    if (this.persistentFilters.has(key)) {
                        persistentCount++;
                        persistentActiveFilters.push(key);
                    } else {
                        customCount++;
                    }
                    activeFilters.push(key);
                }
            }
        });

        return {
            total: defaultCount + customCount + persistentCount,
            default: defaultCount,
            custom: customCount,
            persistent: persistentCount,
            activeFilters,
            persistentFilters: persistentActiveFilters
        };
    }

    /**
     * Checks if a field is internal (pagination, control, etc) and should be ignored.
     *
     * @private
     * @param {string} key - Field name to check
     * @returns {boolean} True if field is internal, false otherwise
     */
    private isInternalField(key: string): boolean {
        const internalFields = [
            this.fieldMapping.version!,  // versioning
            this.fieldMapping.page!,     // pagination
            this.fieldMapping.size!,      // page size
            this.fieldMapping.sort!      // sorting
        ];

        return internalFields.includes(key);
    }

    /**
     * Saves the initial filter state after child component initialization.
     * This method should be called automatically after child's onInit().
     *
     * @private
     */
    private saveInitialFiltersState(): void {
        this.initialFilters = JSON.parse(JSON.stringify(this.filters));
    }

    /**
     * Applies URL parameters only during initialization (executes only if useQueryParams = true).
     *
     * @private
     */
    private applyUrlOnInit(): void {
        const urlParams = this.activatedRoute.snapshot.queryParams;

        if (!this.isObjectEmpty(urlParams)) {
            // Filtrar parâmetros ignorados também na inicialização
            const filteredParams = this.filterIgnoredParams(urlParams);

            if (!this.isObjectEmpty(filteredParams)) {
                // Normalize params before conversion (e.g., ensure arrays for multi-select fields)
                const normalizedParams = this.normalizeQueryParams(filteredParams);
                const convertedParams = this.convertAndValidateParams(normalizedParams);

                // URL has priority - overwrites everything
                Object.keys(convertedParams).forEach(key => {
                    this.filters[key] = convertedParams[key];
                });

                this.currentPage = (convertedParams[this.fieldMapping.page!] !== undefined ? convertedParams[this.fieldMapping.page!] : 0) + 1;
                this.reconstructSortFromParams(convertedParams);
            }
        }

        // First findAll call (only if autoLoad is active)
        if (this.config.autoLoad) {
            this.setLoading(true);
            this.findAll(this.filters);
        }
    }

    /**
     * Reconstructs the currentSort state from URL parameters.
     * Called during initialization to restore sort state from query params.
     */
    private reconstructSortFromParams(params: any): void {
        const sortParam = params[this.fieldMapping.sort!];

        if (sortParam && typeof sortParam === 'string') {
            if (sortParam.includes(';')) {
                // Multi-sort: "field1,asc;field2,desc"
                this.currentSort = sortParam.split(';').map((sortStr: string, index: number) => {
                    const [field, direction] = sortStr.split(',');
                    return {
                        field,
                        direction: direction as 'asc' | 'desc',
                        priority: index + 1
                    };
                });
            } else {
                // Single sort: "field,direction"
                const [field, direction] = sortParam.split(',');
                this.currentSort = [{
                    field,
                    direction: direction as 'asc' | 'desc',
                    priority: 1
                }];
            }
        }
    }

    /**
     * Listener for URL changes (executes only if useQueryParams = true).
     *
     * @private
     */
    private listenQueryParams(): void {
        const debounceTime = this.config.debounceTime || AppPaginationBaseAbstract.DEFAULT_DEBOUNCE_TIME;

        this.queryParams$ = this.activatedRoute.queryParams.pipe(
            debounce(() => this.firstTime ? timer(AppPaginationBaseAbstract.FIRST_DEBOUNCE_TIME) : timer(debounceTime))
        ).subscribe({
            next: (params) => this.handleQueryParams(params),
            error: (error) => this.handleSubscriptionError(error)
        });
    }

    /**
     * Handles query parameter changes from URL.
     *
     * @private
     * @param {any} params - Query parameters object
     */
    private handleQueryParams(params: any): void {
        if (this.firstTime) {
            // First execution already handled in applyUrlOnInit
            // Don't set loading here - it's already managed by applyUrlOnInit
            this.firstTime = false;
            return;
        }

        // Set loading BEFORE any other logic (only for subsequent URL changes)
        this.setLoading(true);

        try {
            // Filtrar parâmetros ignorados
            const filteredParams = this.filterIgnoredParams(params);
            // Subsequent URL changes
            if (!this.isObjectEmpty(filteredParams)) {
                // Normalize params before conversion (e.g., ensure arrays for multi-select fields)
                const normalizedParams = this.normalizeQueryParams(filteredParams);
                const convertedParams = this.convertAndValidateParams(normalizedParams);

                Object.keys(convertedParams).forEach(key => {
                    this.filters[key] = convertedParams[key];
                });

                this.currentPage = (convertedParams[this.fieldMapping.page!] !== undefined ? convertedParams[this.fieldMapping.page!] : 0) + 1;
                this.reconstructSortFromParams(convertedParams);
                this.onNextQueryParams(convertedParams);
            }

            this.findAll(this.filters);
        } catch (error) {
            console.error('Error processing query params:', error);
            this.setLoading(false);
        }
    }

    /**
     * Sets parameters that should be ignored in URL.
     * These parameters will not be included in the query string.
     */
    protected setIgnoredParams(paramNames: string[]): void {
        this.ignoredParams = new Set(paramNames);
    }

    /**
     * Filters out ignored parameters from the provided object.
     * @param params
     * @private
     */
    private filterIgnoredParams(params: any): any {
        const filtered: any = {};
        Object.keys(params).forEach(key => {
            if (!this.ignoredParams.has(key)) {
                filtered[key] = params[key];
            }
        });
        return filtered;
    }

    /**
     * Updates URL immediately (executes only if useQueryParams = true).
     *
     * @private
     */
    private updateURL(): void {
        if (!this.config.useQueryParams) return;

        const cleanFilters = this.removeEmptyFilters(this.filters);
        this.routeService.updateQueryParams(cleanFilters);
    }

    /**
     * Updates URL replacing all parameters (for reset) (executes only if useQueryParams = true).
     *
     * @private
     */
    private replaceURL(): void {
        if (!this.config.useQueryParams) return;

        const cleanFilters = this.removeEmptyFilters(this.filters);
        this.routeService.clearAllQueryParamsExcept(cleanFilters);
    }

    /**
     * Converts and validates URL parameters.
     *
     * @private
     * @param {any} params - Raw URL parameters
     * @returns {any} Converted and validated parameters
     */
    private convertAndValidateParams(params: any): any {
        const convertedParams = this.convertIntObj(params);

        if (convertedParams[this.fieldMapping.size!] !== undefined && !this.isValidPageSize(convertedParams[this.fieldMapping.size!])) {
            convertedParams[this.fieldMapping.size!] = AppPaginationBaseAbstract.DEFAULT_PAGE_SIZE;
        }

        if (convertedParams[this.fieldMapping.page!] !== undefined && convertedParams[this.fieldMapping.page!] < 0) {
            convertedParams[this.fieldMapping.page!] = 0;
        }

        return convertedParams;
    }

    /**
     * Handles subscription errors for query parameters.
     *
     * @private
     * @param {any} error - Error object
     */
    private handleSubscriptionError(error: any): void {
        console.error('Error in query params subscription:', error);
        this.setLoading(false);
    }

    /**
     * Unsubscribes from query parameters subscription.
     *
     * @private
     */
    private unsubscribeFromQueryParams(): void {
        if (this.queryParams$) {
            this.queryParams$.unsubscribe();
            this.queryParams$ = undefined;
        }
    }

    /**
     * Removes empty filters from the filters object for URL cleanup.
     *
     * @private
     * @param {any} filters - Filters object to clean
     * @returns {any} Clean filters object
     */
    private removeEmptyFilters(filters: any): any {
        const cleanFilters: any = {};

        Object.keys(filters).forEach(key => {
            const value = filters[key];

            // Always include version if it has value
            if (key === this.fieldMapping.version && value) {
                cleanFilters[key] = value;
                return;
            }

            // Keep empty strings, empty arrays, number 0, false, etc.
            if (value !== undefined) {
                cleanFilters[key] = value;
            }
        });

        return cleanFilters;
    }

    /**
     * Resets pagination state to default values.
     *
     * @private
     */
    private resetPaginationState(): void {
        this.pagination = {...this.getDefaultPagination()};
    }

    /**
     * Validates if a page number is valid.
     *
     * @private
     * @param {number} page - Page number to validate
     * @returns {boolean} True if page is valid, false otherwise
     */
    private isValidPage(page: number): boolean {
        return Number.isInteger(page) && page >= AppPaginationBaseAbstract.MIN_PAGE;
    }

    /**
     * Validates if a page size is valid.
     *
     * @private
     * @param {number} size - Page size to validate
     * @returns {boolean} True if page size is valid, false otherwise
     */
    private isValidPageSize(size: number): boolean {
        return Number.isInteger(+size) && size > 0 && size <= 1000;
    }

    /**
     * Converts object values to appropriate types (string to number/boolean when possible).
     *
     * @public
     * @param {any} obj - Object to convert
     * @returns {any} Object with converted values
     *
     * @example
     * ```typescript
     * const converted = this.convertIntObj({ page: '1', active: 'true' });
     * // Result: { page: 1, active: true }
     * ```
     */
    convertIntObj(obj: any): any {
        const newObj: any = {};

        if (!obj || typeof obj !== 'object') {
            return newObj;
        }

        Object.keys(obj).forEach((key: string) => {
            try {
                let value = obj[key];

                if (Array.isArray(value)) {
                    newObj[key] = [...value];
                    return;
                }

                if (typeof value === 'string') {
                    value = value.trim();
                }

                if (this.isEmptyValue(value)) {
                    return;
                }

                newObj[key] = this.convertValue(value);
            } catch (error) {
                console.warn(`Error converting value for key '${key}':`, error);
            }
        });

        return newObj;
    }

    /**
     * Checks if a value is considered empty.
     *
     * @private
     * @param {any} value - Value to check
     * @returns {boolean} True if value is empty, false otherwise
     */
    private isEmptyValue(value: any): boolean {
        return value === undefined ||
            value === null ||
            value === '' ||
            (typeof value === 'string' && value.trim() === '');
    }

    /**
     * Converts a string value to appropriate type (number, boolean, or keeps as string).
     *
     * @private
     * @param {any} value - Value to convert
     * @returns {any} Converted value
     */
    private convertValue(value: any): any {
        if (typeof value !== 'string') {
            return value;
        }

        const numericValue = Number(value);
        if (!isNaN(numericValue) && isFinite(numericValue)) {
            return numericValue;
        }

        if (value.toLowerCase() === 'true') return true;
        if (value.toLowerCase() === 'false') return false;

        return value;
    }

    /**
     * Checks if current page is the first page.
     *
     * @public
     * @returns {boolean} True if on first page, false otherwise
     */
    get isFirstPage(): boolean {
        return this.currentPage === 1;
    }

    /**
     * Checks if current page is the last page.
     *
     * @public
     * @returns {boolean} True if on last page, false otherwise
     */
    get isLastPage(): boolean {
        if (!this.pagination.itemsAmount || !this.filters[this.fieldMapping.size!]) return false;
        const totalPages = Math.ceil(this.pagination.itemsAmount / this.filters[this.fieldMapping.size!]);
        return this.currentPage >= totalPages;
    }

    /**
     * Checks if an object is empty.
     *
     * @private
     * @param {any} obj - Object to check
     * @returns {boolean} True if object is empty, false otherwise
     */
    private isObjectEmpty(obj: any): boolean {
        return Object.keys(obj).length === 0 && obj.constructor === Object;
    }
}
